/**
 * The collections a support screen can be told about, by name.
 *
 * ── why a name and not a timer ─────────────────────────────────────────────
 *
 * Ten screens asked their endpoint again every twenty seconds whether anything
 * had changed, and the answer was almost always no. Twenty open tabs is a load
 * profile: a request every second against a pool this box shares with the
 * scanners, all day, to learn nothing. Polling a database to find out whether
 * it changed is asking the wrong end — the end that *made* the change already
 * knows.
 *
 * The API has said so for cases since the board stopped lying all morning:
 * one server-sent stream, and the panel re-reads only when something moved.
 * These are the rest of the collections that stream can name, so every screen
 * in the console works the one way rather than half of them on timers.
 *
 * ── the rule that keeps this honest ────────────────────────────────────────
 *
 * A screen that watches a name nothing ever publishes is worse than a screen
 * that polls: it looks live and is frozen. `scripts/live-tables-have-publishers.mjs`
 * refuses that in `check` — every name watched here must be announced somewhere
 * in the API, which is the same rule `queues-have-workers.mjs` applies to a
 * queue nobody consumes.
 */
export const PANEL_TABLE = {
  /** Scan runs — /scans, and the count on the rail. */
  SCANS: 'scan-runs',
  /** Everything the product has told a client — /notification-log. */
  NOTIFICATIONS: 'notification-log',
  /** Reference images and their fingerprints — /reference-assets. */
  REFERENCE_ASSETS: 'reference-assets',
  /** Invitations to join an account — /account-invites. */
  ACCOUNT_INVITES: 'account-invites',
  /** Metered usage against a plan — /usage-quotas. */
  USAGE_QUOTAS: 'usage-quotas',
  /** What search actually cost — /spend and /search-requests. */
  SEARCH: 'search',
  /** Client conversations — /support. */
  SUPPORT: 'support-threads',
  /** Stripe's side of things — /billing. */
  BILLING: 'billing-events',
  /** Invitations and the rewards they earned — /referrals. */
  REFERRALS: 'referrals',
  /** Bio-code checks, announced by the browser worker when it finishes one — /ownership. */
  OWNERSHIP: 'profile-ownership',
} as const;

export type PanelTable = (typeof PANEL_TABLE)[keyof typeof PANEL_TABLE];
